// Feature 039: US5 — Column filter dropdown with multi-select checkboxes and Apply/Clear
// Rendered via React Portal so it is never clipped by table/grid overflow ancestors.
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ColumnFilterDropdownProps {
  options: string[];
  isLoading?: boolean;
  activeValues?: string[];
  onFilterChange: (values: string[] | null) => void;
}

export default function ColumnFilterDropdown({
  options,
  isLoading = false,
  activeValues,
  onFilterChange,
}: ColumnFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [pendingValues, setPendingValues] = useState<string[]>([]);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef  = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLInputElement>(null);
  const activeValuesRef = useRef(activeValues);
  activeValuesRef.current = activeValues;

  // 300 ms debounce for search
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Close on outside click — must check both the trigger button and the portal panel
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      const inButton = buttonRef.current?.contains(target) ?? false;
      const inPanel  = panelRef.current?.contains(target) ?? false;
      if (!inButton && !inPanel) setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  // Calculate panel position and reset state when dropdown opens
  useEffect(() => {
    if (!open) return;

    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Prefer opening downward; flip up when too close to viewport bottom
      const panelHeight = 320; // max-h-80 = 20rem = 320px
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= panelHeight
        ? rect.bottom + 4
        : rect.top - panelHeight - 4;
      setPanelPos({ top, left: rect.left });
    }

    setPendingValues(activeValuesRef.current ?? []);
    setSearch('');
    setDebounced('');
    // Focus the search input after the portal mounts (next tick)
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = debounced.length >= 1
    ? options.filter(o => o.toLowerCase().includes(debounced.toLowerCase()))
    : options;

  const isActive = !!(activeValues && activeValues.length > 0);

  function togglePending(opt: string) {
    setPendingValues(prev =>
      prev.includes(opt) ? prev.filter(v => v !== opt) : [...prev, opt],
    );
  }

  function handleApply() {
    onFilterChange(pendingValues.length > 0 ? pendingValues : null);
    setOpen(false);
  }

  function handleClear() {
    setPendingValues([]);
  }

  const panel = open ? (
    <div
      ref={panelRef}
      style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, zIndex: 9999 }}
      className="min-w-[200px] max-h-80 flex flex-col bg-white border border-gray-200 rounded shadow-lg overflow-hidden"
    >
      {/* Search bar — pinned at top */}
      <div className="p-2 border-b flex-shrink-0">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search…"
          className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-primary"
        />
      </div>

      {/* Value list — scrollable */}
      <ul className="flex-1 min-h-0 overflow-y-auto text-sm">
        {isLoading ? (
          <li className="px-3 py-2 text-gray-400 italic">Loading…</li>
        ) : visible.length === 0 ? (
          <li className="px-3 py-2 text-gray-400 italic">No results</li>
        ) : (
          visible.map(opt => {
            const checked = pendingValues.includes(opt);
            return (
              <li key={opt}>
                <label className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-100 ${
                  checked ? 'bg-brand-accent/10' : ''
                }`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => togglePending(opt)}
                    className="accent-[#6B3FA0]"
                  />
                  <span className={checked ? 'font-medium text-brand-accent' : ''}>{opt}</span>
                </label>
              </li>
            );
          })
        )}
      </ul>

      {/* Apply / Clear — pinned at bottom */}
      <div className="flex items-center justify-between gap-2 px-2 py-2 border-t bg-gray-50 flex-shrink-0">
        <button
          type="button"
          onClick={handleClear}
          className="text-xs text-gray-500 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={handleApply}
          className="text-xs font-medium bg-brand-accent text-brand-text-inverse px-3 py-1 rounded hover:bg-brand-accent-hover"
        >
          Apply
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-label={isActive ? `Filter active: ${activeValues!.join(', ')}` : 'Open filter'}
        className={`ml-1 p-0.5 rounded hover:bg-gray-200 transition-colors ${
          isActive ? 'text-brand-accent' : 'text-gray-400'
        }`}
        title={isActive ? `Filtered: ${activeValues!.join(', ')}` : 'Filter'}
      >
        {/* Funnel icon */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
          <path fillRule="evenodd" d="M2 4a1 1 0 011-1h14a1 1 0 01.707 1.707L13 11.414V16a1 1 0 01-.553.894l-4 2A1 1 0 017 18v-6.586L2.293 4.707A1 1 0 012 4z" clipRule="evenodd" />
        </svg>
      </button>

      {createPortal(panel, document.body)}
    </div>
  );
}

